/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 29/04/2025 17:35
 *
 */

import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  ActionEvent,
  AsyncTableManager,
  DataModel,
  DataTableOptions,
  DataTableOptionsBuilder,
  RestService,
  StandardAction,
  StandardEntity,
} from '@infodata-group/inf-commons';
import type { Factory } from '@infodata-group/inf-commons';
import type { ICrud } from '../interface/crud.interface';
import { StandardPageDetailComponent } from '../standard-page-detail/standard-page-detail.component';
import { MasterDetailComponent } from '@infodata-group/inf-commons-desktop';

@Component({
  selector: 'infl-standard-page-master-detail',
  standalone: true,
  imports: [StandardPageDetailComponent, MasterDetailComponent],
  templateUrl: './standard-page-master-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './standard-page-master-detail.component.scss',
})
export class StandardPageMasterDetailComponent<T extends StandardEntity>
  implements OnChanges
{
  private readonly restService = inject(RestService);

  @Input({ required: true }) model: DataModel;
  @Input({ required: true }) factory: Factory<T>;
  @Input({ required: true }) CRUD: ICrud;
  @Input({ required: true }) displayDetails: boolean;

  @Output() actionClick = new EventEmitter<ActionEvent>();

  public entity: T;
  protected tableManager: AsyncTableManager<T>;
  protected tableOptions: DataTableOptions;

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.hasOwn(changes, 'CRUD')) {
      this.tableOptions = this.displayDetails
        ? this.CRUD.create
          ? new DataTableOptionsBuilder('')
              .setConfigMasterDetail()
              .addCreateAction()
              .build()
          : new DataTableOptionsBuilder('').setConfigMasterDetail().build()
        : this.CRUD.create
          ? new DataTableOptionsBuilder('')
              .setConfigDataTable()
              .addCreateAction()
              .build()
          : new DataTableOptionsBuilder('').setConfigDataTable().build();
    }
    if (Object.hasOwn(changes, 'model')) {
      this.tableOptions.storageKey = `standard-tab-${this.model.id}`;
      this.tableManager = new AsyncTableManager(
        this.model,
        this.restService.getAll(this.factory, { showLoading: false }),
      );
    }
  }

  onActionClick(event: ActionEvent) {
    if (event.action.name === 'deleteAndRefresh') {
      this.tableManager.notify(true);
      return;
    }
    this.actionClick.emit(event);
  }

  onRowClick(event: T) {
    // console.log(event);
    if (!this.displayDetails) {
      this.actionClick.emit(
        new ActionEvent('click', new StandardAction('consult', ''), event),
      );
    }
  }

  onSelectRow($event: T) {
    this.entity = null;
    setTimeout(() => {
      this.entity = $event;
    });
  }

  refresh(id?: string) {
    if (id) {
      const sub = this.tableManager.getEntities().subscribe({
        next: () => {
          const entity = this.tableManager.data.find((v) => v.getId() === id);
          if (entity) {
            this.tableManager.selectedRow = entity;
            this.entity = entity;
          }
          sub.unsubscribe();
        },
        error: () => {
          sub.unsubscribe();
        },
      });
    }
    this.tableManager.notify(true);
  }
}
